import { loadSettings } from './settingsManager';

/** Phaser.Sound.BaseSoundManagerの最小インターフェース */
interface SoundManager {
  add(key: string): Sound;
}

/** Phaser.Sound.BaseSoundの最小インターフェース */
interface Sound {
  play(): void;
  stop(): void;
  pause(): void;
  resume(): void;
  destroy(): void;
  setVolume(volume: number): void;
  setLoop(loop: boolean): void;
  once(event: string, callback: () => void): void;
}

/** 現在再生中のBGM */
let currentBgm: Sound | null = null;

/** 再生中のSEを追跡するセット */
const activeSeSounds = new Set<Sound>();

/**
 * BGMを再生する
 * 既存のBGMがあれば停止してから新しいBGMをループ再生する
 */
export function playBgm(soundManager: SoundManager, key: string): void {
  // 既存BGM停止
  if (currentBgm) {
    currentBgm.stop();
    currentBgm.destroy();
    currentBgm = null;
  }

  try {
    const settings = loadSettings();
    const volume = Math.pow(settings.bgmVolume / 100, 3);

    const sound = soundManager.add(key);
    sound.setLoop(true);
    sound.setVolume(volume);
    sound.play();

    currentBgm = sound;
  } catch {
    // 音声がキャッシュにない場合は無視（プレースホルダー音声対応）
  }
}

/**
 * BGMを一時停止する（バックグラウンド移行時に使用）
 */
export function pauseBgm(): void {
  if (!currentBgm) return;
  currentBgm.pause();
}

/**
 * 一時停止中のBGMを再開する（フォアグラウンド復帰時に使用）
 */
export function resumeBgm(): void {
  if (!currentBgm) return;
  currentBgm.resume();
}

/**
 * BGMを停止・破棄する
 */
export function stopBgm(): void {
  if (!currentBgm) return;

  currentBgm.stop();
  currentBgm.destroy();
  currentBgm = null;
}

/**
 * 再生中のBGMの音量をリアルタイムで変更する
 * @param volume 0〜100の整数
 */
export function setBgmVolume(volume: number): void {
  if (!currentBgm) return;

  currentBgm.setVolume(Math.pow(volume / 100, 3));
}

/**
 * SEをワンショット再生する
 */
export function playSe(soundManager: SoundManager, key: string): void {
  try {
    const settings = loadSettings();
    const volume = settings.seVolume / 100;

    const sound = soundManager.add(key);
    sound.setVolume(volume);
    sound.play();
    activeSeSounds.add(sound);
    sound.once('complete', () => {
      activeSeSounds.delete(sound);
      sound.destroy();
    });
  } catch {
    // 音声がキャッシュにない場合は無視（プレースホルダー音声対応）
  }
}

/**
 * 再生中のSEをすべて停止・破棄する（シーン終了時に呼び出す）
 */
export function stopAllSe(): void {
  activeSeSounds.forEach(sound => {
    try {
      sound.stop();
      sound.destroy();
    } catch {
      // 既に破棄済みの場合は無視
    }
  });
  activeSeSounds.clear();
}

/**
 * テスト用: モジュール状態をリセットする
 */
export function _resetForTest(): void {
  currentBgm = null;
}
